/**
 * Tamaños (variantes) y agregados de un plato.
 *
 * Lo usan el POS y el menú público para que ambos cobren exactamente lo mismo:
 * si esta lógica vive en un solo lugar, no puede divergir entre caja y web.
 *
 *   variantes: [{ id, nombre, precio }]  → precio ABSOLUTO del tamaño
 *   agregados: [{ id, nombre, precio }]  → precio que se SUMA
 */

/**
 * Agregados finales de un plato: los de los grupos que tenga marcados
 * (definidos una sola vez y reutilizables) más los propios de ese plato.
 * Si un nombre se repite, se conserva el primero para no ofrecerlo dos veces.
 */
export function resolverAgregados(item, grupos = []) {
  const ids = item?.gruposAgregados || [];
  const deGrupos = ids.flatMap(gid => {
    const g = grupos.find(x => x.id === gid);
    return g?.activo === false ? [] : (g?.items || []);
  });
  const todos = [...deGrupos, ...(item?.agregados || [])];
  const vistos = new Set();
  return todos.filter(a => {
    const k = (a?.nombre || '').trim().toLowerCase();
    if (!k || vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
}

/** ¿Este ítem necesita que el cliente elija algo antes de agregarlo? */
export function tieneOpciones(item) {
  return (item?.variantes?.length > 0) || (item?.agregados?.length > 0);
}

/** Precio final: el del tamaño elegido (o el base si no hay tamaños) + los agregados. */
export function precioConOpciones(precioBase, variante, agregados = []) {
  const base = variante ? Number(variante.precio) || 0 : Number(precioBase) || 0;
  return base + agregados.reduce((s, a) => s + (Number(a.precio) || 0), 0);
}

/** Nombre para cocina y boleta: "Pizza pepperoni (Familiar) + Choclo, Mechada". */
export function nombreConOpciones(nombre, variante, agregados = []) {
  let n = variante ? `${nombre} (${variante.nombre})` : nombre;
  if (agregados.length > 0) n += ` + ${agregados.map(a => a.nombre).join(', ')}`;
  return n;
}

/**
 * Id de la línea del carrito. Dos tamaños distintos son líneas separadas,
 * pero los mismos agregados elegidos en distinto orden son la misma línea
 * (por eso el sort) y suman cantidad en vez de duplicarse.
 */
export function idLineaCarrito(baseId, variante, agregados = []) {
  return `opt_${baseId}_${variante?.id || 'base'}_${agregados.map(a => a.id).sort().join('-')}`;
}

/** Precio "desde" que se muestra en las tarjetas: el tamaño más barato. */
export function precioDesde(item) {
  const precios = (item?.variantes || []).map(v => Number(v.precio) || 0).filter(p => p > 0);
  return precios.length > 0 ? Math.min(...precios) : (Number(item?.precioVenta) || 0);
}
